/* ═══════════════════════════════════════════════════════════════
   REVIEW — the state behind Review mode (docs/SYNC_PLAN.md §2.3).

   Loads the master updates waiting on one target, holds the decisions
   (each one remembered on the row as it is taken), and Finish:

     1. planReview(current editor blocks, changes, decisions)
     2. ONE editor transaction → the document becomes plan.blocks
        (minimal node replacement, one undo step, through the editor so
        a shared document receives it like any edit)
     3. service.finish(rowId, decisions, plan.blocks)
     4. toast "8 changes applied, 3 rejected" → leave Review

   Undecided changes stay pending on the row, untouched (guarantee 1).
   The service is injected — the hosts pass `syncService` from the adapter.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import type { ContentBlock } from '../../shared/types';
import type { Change, Decision, ReviewPlan } from '../../shared/sync';
import { docToBlocks } from '../designer/schema';
import { replaceDocWithBlocks } from './applyBlocks';
import {
  finishBlocker,
  hashBlocks,
  isAlreadyResolved,
  prepareFinish,
  type AppliedMarker,
} from './finishGuard';
import { useTableSync } from '../hooks/useTableSync';
import { pendingChangeCount } from './feedSummary';
import { readAppliedMarker, writeAppliedMarker } from './syncAdapter';
import type {
  FeedChange,
  ReviewService,
  SyncFeedRow,
  SyncTargetKind,
  UpstreamPending,
} from './types';

/* ─── Walking a list of changes (shared with the self-review walk) ─── */

export interface ChangeWalker {
  index: number;
  current: Change | null;
  setCurrentId: (id: string) => void;
  next: () => void;
  prev: () => void;
}

export function useChangeWalker(items: Change[]): ChangeWalker {
  const [currentId, setCurrentId] = React.useState<string | null>(null);
  const found = currentId ? items.findIndex((c) => c.id === currentId) : -1;
  const index = found >= 0 ? found : items.length ? 0 : -1;
  const current = index >= 0 ? items[index] : null;
  const next = React.useCallback(() => {
    if (!items.length) return;
    setCurrentId(items[Math.min(items.length - 1, index + 1)].id);
  }, [items, index]);
  const prev = React.useCallback(() => {
    if (!items.length) return;
    setCurrentId(items[Math.max(0, index - 1)].id);
  }, [items, index]);
  return { index, current, setCurrentId, next, prev };
}

/* ─── Helpers ─── */

export { pendingChangeCount };

export function stripDecision(c: FeedChange): Change {
  const { decision: _decision, ...rest } = c;
  return rest;
}

function initialDecisions(row: SyncFeedRow | null): Record<string, Decision> {
  const out: Record<string, Decision> = {};
  for (const c of row?.changes || []) if (c.decision) out[c.id] = c.decision;
  return out;
}

/** Bump `moduleStamp.moduleVersion` on every block of one module. */
export function stampModuleVersion(
  blocks: ContentBlock[],
  moduleId: string,
  version: number
): ContentBlock[] {
  return blocks.map((b) => {
    const stamp = (b as { moduleStamp?: { moduleId?: string; moduleVersion?: number } })
      .moduleStamp;
    if (!stamp || stamp.moduleId !== moduleId || stamp.moduleVersion === version) return b;
    return { ...b, moduleStamp: { ...stamp, moduleVersion: version } } as ContentBlock;
  });
}

export function finishSummary(plan: Pick<ReviewPlan, 'applied' | 'rejected'>): string {
  const a = `${plan.applied} change${plan.applied === 1 ? '' : 's'} applied`;
  return plan.rejected ? `${a}, ${plan.rejected} rejected` : a;
}

/* ─── The hook ─── */

export interface UseReviewOptions {
  targetKind: SyncTargetKind;
  /** null = nothing open (the hook idles). */
  targetId: string | null;
  service: ReviewService;
  editor: Editor | null;
  /** specs.publish — without it the walk is read-only. */
  canDecide: boolean;
  toast: (msg: string, isError?: boolean) => void;
  /** False while the host's editor shows something other than the live
   *  document (a History snapshot, an issuance) — Finish refuses then. */
  isLiveDocument?: boolean;
  /** The "applied locally" marker store (tests inject their own). */
  markers?: AppliedMarkerStore;
}

export interface AppliedMarkerStore {
  read: (rowId: string) => AppliedMarker | null;
  write: (rowId: string, marker: AppliedMarker | null) => void;
}

const PREFS_MARKERS: AppliedMarkerStore = { read: readAppliedMarker, write: writeAppliedMarker };

export interface ReviewApi {
  loading: boolean;
  /** The feed has answered at least once for this target. */
  loaded: boolean;
  loadError: string | null;
  /** Pending rows for this target. */
  rows: SyncFeedRow[];
  upstreamPending: UpstreamPending[];
  pendingCount: number;
  reload: () => void;

  active: boolean;
  start: (rowId?: string) => void;
  leave: () => void;

  row: SyncFeedRow | null;
  /** The decidable changes (reconciled excluded). */
  items: Change[];
  reconciled: Change[];
  decisions: Record<string, Decision>;
  walker: ChangeWalker;
  canDecide: boolean;
  decide: (id: string, d: Decision) => void;
  acceptAllRemaining: () => void;
  canUndoAcceptAll: boolean;
  undoAcceptAll: () => void;
  finish: () => Promise<void>;
  busy: boolean;
  error: string | null;
}

/* ─── The shared state behind every Review (specs and formas) ─── */

export interface ReviewCore {
  loading: boolean;
  loadError: string | null;
  rows: SyncFeedRow[];
  setRows: React.Dispatch<React.SetStateAction<SyncFeedRow[]>>;
  upstreamPending: UpstreamPending[];
  reload: () => void;
  /** The feed has answered for the current target. */
  loaded: boolean;
  active: boolean;
  start: (rowId?: string) => void;
  leave: () => void;
  row: SyncFeedRow | null;
  items: Change[];
  reconciled: Change[];
  decisions: Record<string, Decision>;
  walker: ChangeWalker;
  decide: (id: string, d: Decision) => void;
  acceptAllRemaining: () => void;
  canUndoAcceptAll: boolean;
  undoAcceptAll: () => void;
  error: string | null;
  setError: (e: string | null) => void;
}

/**
 * The feed rows for one target, the walk, and the decisions — each answer
 * remembered on the row as it is taken. `useReview` (project specs) and
 * `useFormaReview` (project formas) differ only in how Finish applies.
 * `liveTables` = the tables whose broadcasts mean "re-fetch".
 */
export function useReviewCore({
  targetKind,
  targetId,
  service,
  canDecide,
  liveTables,
}: {
  targetKind: SyncTargetKind;
  targetId: string | null;
  service: ReviewService;
  canDecide: boolean;
  liveTables: string[];
}): ReviewCore {
  const [rows, setRows] = React.useState<SyncFeedRow[]>([]);
  const [upstreamPending, setUpstreamPending] = React.useState<UpstreamPending[]>([]);
  const [loading, setLoading] = React.useState(false);
  // The target whose feed has answered at least once (loading starts false
  // before the first fetch — consumers must not read "empty" off that).
  const [loadedFor, setLoadedFor] = React.useState<string | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [rev, bump] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    if (!targetId) {
      setRows([]);
      setUpstreamPending([]);
      return;
    }
    let live = true;
    setLoading(true);
    setLoadError(null);
    service
      .feedList({ targetKind, targetId })
      .then((res) => {
        if (!live) return;
        setRows((res.rows || []).filter((r) => r.targetId === targetId && r.status === 'pending'));
        setUpstreamPending((res.upstreamPending || []).filter((u) => u.targetId === targetId));
      })
      .catch((err: unknown) => {
        if (!live) return;
        setRows([]);
        setUpstreamPending([]);
        setLoadError(
          err instanceof Error && err.message
            ? `Could not load master updates: ${err.message}. Try again in a moment.`
            : 'Could not load master updates. Try again in a moment.'
        );
      })
      .finally(() => {
        if (live) {
          setLoading(false);
          setLoadedFor(targetId);
        }
      });
    return () => {
      live = false;
    };
  }, [service, targetKind, targetId, rev]);

  // Live: the server broadcasts sync rows as table `sync_feed` (and the copy
  // itself under its own table); re-fetch when either changes.
  useTableSync(liveTables, bump);

  const [active, setActive] = React.useState(false);
  const [rowId, setRowId] = React.useState<string | null>(null);
  const row = React.useMemo(
    () => rows.find((r) => r.id === rowId) ?? (active ? (rows[0] ?? null) : null),
    [rows, rowId, active]
  );
  const [decisions, setDecisions] = React.useState<Record<string, Decision>>({});
  const [bulkUndo, setBulkUndo] = React.useState<Record<string, Decision> | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // A different row (or none) → its own remembered decisions.
  const loadedRowRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const id = row?.id ?? null;
    if (loadedRowRef.current === id) return;
    loadedRowRef.current = id;
    setDecisions(initialDecisions(row));
    setBulkUndo(null);
    setError(null);
  }, [row]);

  const items = React.useMemo(
    () => (row?.changes || []).filter((c) => c.kind !== 'reconciled').map(stripDecision),
    [row]
  );
  const reconciled = React.useMemo(
    () => (row?.changes || []).filter((c) => c.kind === 'reconciled').map(stripDecision),
    [row]
  );
  const walker = useChangeWalker(items);

  const persist = React.useCallback(
    (id: string, next: Record<string, Decision>) => {
      // The server MERGES the map it is sent, so an answer that was cleared
      // (Undo accept all) goes out as an explicit null.
      const full: Record<string, Decision | null> = { ...next };
      for (const c of items) if (!full[c.id]) full[c.id] = null;
      service.decide(id, full).catch(() => {
        setError(
          'Could not save that decision. It is kept here — check your connection, then Finish.'
        );
      });
    },
    [service, items]
  );

  const decisionsRef = React.useRef(decisions);
  decisionsRef.current = decisions;
  const commit = React.useCallback(
    (next: Record<string, Decision>) => {
      if (!row) return;
      decisionsRef.current = next;
      setDecisions(next);
      persist(row.id, next);
    },
    [row, persist]
  );

  const decide = React.useCallback(
    (id: string, d: Decision) => {
      if (!canDecide || !row) return;
      if (decisionsRef.current[id] === d) return;
      commit({ ...decisionsRef.current, [id]: d });
    },
    [canDecide, row, commit]
  );

  const acceptAllRemaining = React.useCallback(() => {
    if (!canDecide || !row) return;
    const prev = decisionsRef.current;
    const next = { ...prev };
    let changed = false;
    for (const c of items) {
      if (!next[c.id]) {
        next[c.id] = 'accept';
        changed = true;
      }
    }
    if (!changed) return;
    setBulkUndo(prev);
    commit(next);
  }, [canDecide, row, items, commit]);

  const undoAcceptAll = React.useCallback(() => {
    if (!bulkUndo) return;
    commit(bulkUndo);
    setBulkUndo(null);
  }, [bulkUndo, commit]);

  const start = React.useCallback((id?: string) => {
    setRowId(id ?? null);
    setActive(true);
  }, []);
  const leave = React.useCallback(() => {
    setActive(false);
    setRowId(null);
    setError(null);
  }, []);

  return {
    loading,
    loaded: !!targetId && loadedFor === targetId && !loading,
    loadError,
    rows,
    setRows,
    upstreamPending,
    reload: bump,
    active,
    start,
    leave,
    row,
    items,
    reconciled,
    decisions,
    walker,
    decide,
    acceptAllRemaining,
    canUndoAcceptAll: !!bulkUndo,
    undoAcceptAll,
    error,
    setError,
  };
}

const SPEC_LIVE_TABLES = ['sync_feed', 'project_specs'];

export function useReview({
  targetKind,
  targetId,
  service,
  editor,
  canDecide,
  toast,
  isLiveDocument = true,
  markers = PREFS_MARKERS,
}: UseReviewOptions): ReviewApi {
  const core = useReviewCore({
    targetKind,
    targetId,
    service,
    canDecide,
    liveTables: SPEC_LIVE_TABLES,
  });
  const { row, items, decisions, leave, setRows, setError, reload: bump } = core;
  const [busy, setBusy] = React.useState(false);

  const finish = React.useCallback(async () => {
    if (!canDecide || !row) return;
    const blocked = finishBlocker(!!editor && !editor.isDestroyed, isLiveDocument);
    if (blocked || !editor) {
      setError(blocked);
      toast(blocked || 'The document is not ready', true);
      return;
    }
    setBusy(true);
    setError(null);
    let wrote = false;
    try {
      // Idempotent across reloads: a document that already holds this
      // Finish (marker hash matches) is not planned onto a second time.
      const current = docToBlocks(editor.getJSON());
      const prep = prepareFinish(current, items, decisions, markers.read(row.id));
      const plan = prep.plan;
      let content = plan.blocks;
      if (!prep.skipApply && plan.applied > 0) {
        // Reject-only Finish (applied === 0) writes nothing: the draft must
        // not turn "Pending publish" for a no-op. The row still resolves.
        // Module reviews: the server never touches the copy's blocks, so the
        // accepted module's blocks take the new version here, in the same write.
        if (row.upstreamKind === 'module' && row.moduleId && row.moduleToVersion != null) {
          plan.blocks = stampModuleVersion(plan.blocks, row.moduleId, row.moduleToVersion);
        }
        const tr = replaceDocWithBlocks(editor.state, plan.blocks);
        if (tr) editor.view.dispatch(tr);
        content = docToBlocks(editor.getJSON());
        markers.write(row.id, {
          hash: hashBlocks(content),
          applied: plan.applied,
          rejected: plan.rejected,
        });
      }
      wrote = true;
      try {
        await service.finish(row.id, decisions, content);
      } catch (err) {
        if (!isAlreadyResolved(err)) throw err;
      }
      markers.write(row.id, null);
      toast(finishSummary(plan));
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      leave();
      bump();
    } catch {
      setError(
        wrote
          ? 'The changes are in the document, but the review was not recorded. Check your connection, then Finish again.'
          : 'Could not apply the changes. Nothing was changed — try Finish again.'
      );
    } finally {
      setBusy(false);
    }
  }, [canDecide, row, editor, isLiveDocument, items, decisions, service, markers, toast, leave]);

  return {
    loading: core.loading,
    loaded: core.loaded,
    loadError: core.loadError,
    rows: core.rows,
    upstreamPending: core.upstreamPending,
    pendingCount: pendingChangeCount(core.rows),
    reload: bump,
    active: core.active,
    start: core.start,
    leave,
    row,
    items,
    reconciled: core.reconciled,
    decisions,
    walker: core.walker,
    canDecide,
    decide: core.decide,
    acceptAllRemaining: core.acceptAllRemaining,
    canUndoAcceptAll: core.canUndoAcceptAll,
    undoAcceptAll: core.undoAcceptAll,
    finish,
    busy,
    error: core.error,
  };
}
